<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";

const textbookAlignedPoints = [
  "Yeh unit ek narrative prose hai jo authority, pride, cruelty aur poetic justice ke ideas ko explore karta hai.",
  "Story ka conflict Shahpesh aur Khipil ke darmiyan power relation se develop hota hai, jahan builder apni skill aur ruler apni authority ke sath saamne aata hai.",
  "Exam prep me plot sequence, character motives, punishment scene aur moral lesson sab important hote hain.",
];

const chapterCoverage = [
  "Story setup and central conflict",
  "Shahpesh as ruler",
  "Khipil as skilled builder",
  "Punishment and misuse of power",
  "Authority versus craftsmanship",
  "Moral and poetic justice",
  "Ending and moral impression",
];

const quickRevisionPoints = [
  "Story authority aur skill ke clash ko central banati hai.",
  "Shahpesh pride aur harsh power ka symbol hai.",
  "Khipil constructive talent aur human dignity ko represent karta hai.",
  "Punishment justice se zyada domination ko show karti hai.",
  "Narrative ka strongest effect misuse of power ki criticism hai.",
  "Ending moral aur poetic justice ka impression chhodti hai.",
];

const readingQuestions = [
  {
    question: "Who are Shahpesh and Khipil in the story?",
    answer: "Shahpesh ek powerful Persian ruler hai jab ke Khipil ek skilled builder hai. Story in dono ke relation ke through power aur punishment ka conflict develop karti hai.",
  },
  {
    question: "Why does conflict arise between Shahpesh and Khipil?",
    answer: "Conflict is liye paida hota hai kyun ke authority aur talent ek doosre ke saamne aa jate hain. Shahpesh apni power assert karna chahta hai, jab ke Khipil ki ability aur position story me tension create karti hai.",
  },
  {
    question: "What does the punishment in the story reveal about Shahpesh's character?",
    answer: "Punishment Shahpesh ki harshness, pride aur authoritarian nature ko reveal karti hai. Woh justice se zyada control aur fear establish karne me interested nazar aata hai.",
  },
  {
    question: "How does the story show the misuse of power?",
    answer: "Story me punishment aur authority ka use balanced justice ke liye nahi balki domination ke liye hota hai. Isi wajah se ruler ki power moral sympathy lose kar deti hai.",
  },
  {
    question: "Why is Khipil an important figure in the story?",
    answer: "Khipil story ka central victim aur meaningful character hai. Us ke through skill, human dignity aur ruler ke unjust behavior ka contrast saamne aata hai.",
  },
  {
    question: "What moral impression does the story leave on the reader?",
    answer: "Story yeh impression deti hai ke pride aur cruelty lasting respect paida nahi karte. Power agar fairness ke baghair use ho to woh moral failure ban jati hai.",
  },
];

const criticalThinking = [
  {
    question: "How do the actions of Shahpesh move the story toward its tragic or ironic ending?",
    answer: "Shahpesh ke harsh decisions aur controlling attitude story ko steadily conflict ki taraf le jate hain. Us ka punishment-centered approach ending ko tragic aur morally ironic bana deta hai.",
  },
  {
    question: "What is the significance of the title The Punishment of Shahpesh, the Persian, on Khipil, the Builder?",
    answer: "Title pehle hi authority aur victim ka relation define kar deta hai. Is se reader ko samajh aa jata hai ke story ka center punishment, power aur us ke moral consequences honge.",
  },
  {
    question: "How does the story present the contrast between authority and craftsmanship?",
    answer: "Authority Shahpesh me represent hoti hai jab ke craftsmanship Khipil me. Story dikhati hai ke talent aur creation constructive cheezen hain, magar unchecked authority destructive ban sakti hai.",
  },
  {
    question: "What kind of justice does the story finally suggest: legal, moral, or poetic?",
    answer: "Story ka strongest impression moral aur poetic justice ka hai. Even if ruler outwardly powerful ho, narrative reader ko yeh feel karwati hai ke cruelty morally justified nahi hoti.",
  },
];

const mcqs = [
  {
    question: "What does Shahpesh's punishment mainly reveal about his character?",
    options: [
      { label: "a", text: "mercy and patience", verdict: "incorrect", reason: "Story me us ka dominant image harsh aur authoritarian ruler ka hai." },
      { label: "b", text: "pride and misuse of power", verdict: "correct", reason: "Punishment us ke control, pride aur cruelty ko expose karti hai." },
      { label: "c", text: "creative skill and humility", verdict: "incorrect", reason: "Creative skill Khipil se associate hoti hai, Shahpesh se nahi." },
      { label: "d", text: "fear of craftsmanship only", verdict: "incorrect", reason: "Issue sirf skill ka fear nahi, authority ka unjust use bhi hai." },
    ],
  },
  {
    question: "Khipil is important in the story because he represents:",
    options: [
      { label: "a", text: "careless disobedience", verdict: "incorrect", reason: "Story ka emphasis us ki skill aur human dignity par hai, casual disobedience par nahi." },
      { label: "b", text: "talent placed against unjust authority", verdict: "correct", reason: "Khipil builder ke tor par constructive skill ko represent karta hai, jo ruler ki harsh authority se contrast me hai." },
      { label: "c", text: "comic relief", verdict: "incorrect", reason: "Story comic tone par based nahi hai." },
      { label: "d", text: "political victory", verdict: "incorrect", reason: "Narrative ka center moral conflict hai, political success nahi." },
    ],
  },
  {
    question: "The story's strongest final impression is of:",
    options: [
      { label: "a", text: "moral or poetic justice", verdict: "correct", reason: "Narrative reader ko feel karwati hai ke cruelty aur pride morally fail hoti hain." },
      { label: "b", text: "simple entertainment without message", verdict: "incorrect", reason: "Story clearly moral lesson aur power critique rakhti hai." },
      { label: "c", text: "complete approval of harsh punishment", verdict: "incorrect", reason: "Tone punishment ko glorify nahi karti; misuse of power ko expose karti hai." },
      { label: "d", text: "craftsmanship as a weak quality", verdict: "incorrect", reason: "Craftsmanship ko positive and meaningful quality dikhaya gaya hai." },
    ],
  },
];

const answerBuilding = [
  "Summary likhte waqt beginning, conflict, punishment aur moral ending ki order follow karo.",
  "Character answer me Shahpesh ko proud and harsh ruler aur Khipil ko skilled builder ke tor par contrast karo.",
  "Moral lesson me misuse of power, cruelty, pride aur justice ke points zaroor include karo.",
  "Agar title explain karna ho to authority, punishment aur moral consequences ko ek sath mention karo.",
  "Critical thinking answer me constructive skill versus destructive power ka contrast useful hota hai.",
];

usePageSeo({
  title: "The Punishment of Shahpesh, the Persian, on Khipil, the Builder Notes | 11th Class English Unit 10",
  description: "11th class English Unit 10 notes with story summary, character analysis, moral lesson, reading answers and explained MCQs.",
  keywords: "the punishment of shahpesh the persian on khipil the builder notes, 11th class english unit 10, class 11 english chapter 10, shahpesh short questions, shahpesh mcqs",
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th English", item: "/11th/english/" },
    { name: "Prose", item: "/11th/english/prose/" },
    { name: "The Punishment of Shahpesh, the Persian, on Khipil, the Builder", item: "/11th/english/prose/the-punishment-of-shahpesh-the-persian-on-khipil-the-builder/" },
  ],
});
</script>

<template>
  <PageContainer>
    <section class="app-page-stack">
      <header class="app-page-header">
        <p class="app-page-kicker">Unit 10</p>
        <h1 class="app-page-title">The Punishment of Shahpesh, the Persian, on Khipil, the Builder</h1>
        <p class="app-page-intro">
          Yeh page story ke plot, conflict aur moral irony ko exam prep format me organize karta hai. Is unit me punishment ke through power aur justice ka question central hota hai.
        </p>
      </header>

      <section class="english-panel-muted">
        <h2 class="app-section-heading">Textbook-Aligned Snapshot</h2>
        <ul class="app-list">
          <li v-for="item in textbookAlignedPoints" :key="item">{{ item }}</li>
        </ul>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">Unit 10 Coverage Checklist</h2>
        <ul class="app-list-grid">
          <li v-for="item in chapterCoverage" :key="item" class="english-mini-card-muted">
            {{ item }}
          </li>
        </ul>
      </section>

      <section class="english-panel-muted">
        <h2 class="app-section-heading">Quick Revision Points</h2>
        <ul class="app-list">
          <li v-for="item in quickRevisionPoints" :key="item">{{ item }}</li>
        </ul>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">Reading Questions with Answers</h2>
        <div class="mt-4 space-y-4">
          <article
            v-for="item in readingQuestions"
            :key="item.question"
            class="english-inner-card-muted"
          >
            <h3 class="app-title-card">{{ item.question }}</h3>
            <p class="app-rich-text mt-3">{{ item.answer }}</p>
          </article>
        </div>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">Critical Thinking</h2>
        <div class="mt-4 space-y-4">
          <article
            v-for="item in criticalThinking"
            :key="item.question"
            class="english-inner-card-muted"
          >
            <h3 class="app-title-card">{{ item.question }}</h3>
            <p class="app-rich-text mt-3">{{ item.answer }}</p>
          </article>
        </div>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">MCQs with Option Explanation</h2>
        <div class="mt-4 space-y-4">
          <article
            v-for="item in mcqs"
            :key="item.question"
            class="english-inner-card-muted"
          >
            <h3 class="app-title-card">{{ item.question }}</h3>
            <ul class="mt-3 space-y-2 text-sm leading-6 text-slate-700 dark:text-slate-300">
              <li v-for="option in item.options" :key="option.label">
                <span class="font-medium text-slate-900">{{ option.label }}. {{ option.text }}</span>
                <span>{{ option.verdict === "correct" ? " Correct:" : " Not correct:" }} {{ option.reason }}</span>
              </li>
            </ul>
          </article>
        </div>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">Answer-Building Notes</h2>
        <ul class="app-list">
          <li v-for="item in answerBuilding" :key="item">{{ item }}</li>
        </ul>
      </section>
    </section>
  </PageContainer>
</template>





